import { DatePipe, NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { IconComponent } from "@components/icon/icon.component";
import { TranslateDirective } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ActionButtonComponent } from "~app/common/components/action-button/action-button.component";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { Anskaffningsformer, Planeringsstatus, Verksamhetshandelse, VerksamhetshandelseTyp } from "~app/common/models/constants";
import { Kartgeometrier } from "~app/features/map/popups/kartgeometri-popup/kartgeometri.model";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { ArbetsorderProduktvalService } from "../arbetsorder-produktval/arbetsorder-produktval.service";
import { ArbetsorderMediatorService } from "../services/arbetsorder-mediator.service";
import { AtgardPlaneringsstatusService } from "../services/atgard-planeringsstatus.service";
import { AvlaggVaginfoComponent } from "./avlagg-vaginfo/avlagg-vaginfo.component";

@Component({
    selector: "rask-arbetsorder-drivningspunkter",
    templateUrl: "./arbetsorder-drivningspunkter.component.html",
    styleUrls: ["./arbetsorder-drivningspunkter.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        TranslateDirective,
        NgClass,
        CheckboxComponent,
        FormsModule,
        IconComponent,
        DatePipe,
        ActionButtonComponent
    ]
})
export class ArbetsorderDrivningspunkterComponent implements OnInit, OnDestroy {
    arbetsorder: IArbetsorderDetail;
    canEdit: boolean;
    @Input() hamtadrivningspunkter: any;
    latestKopieraDrivningspunkt: Date;
    @Output() updated: EventEmitter<boolean> = new EventEmitter<boolean>();

    private readonly arbetsorderMediatorService = inject(ArbetsorderMediatorService);
    private readonly arbetsorderProductvalService = inject(ArbetsorderProduktvalService);
    private readonly atgardPlaneringsstatusService = inject(AtgardPlaneringsstatusService);
    private readonly modalService = inject(RaskModalService);
    private readonly ngUnsubscribe$ = new Subject<void>();

    arbetsorderHasGeometrier = () => {
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (!this.arbetsorder || !this.arbetsorder.kartgeometrier) {
            return false;
        }
        return true;
    };

    checkKoordinatorVerifierade(): void {
        if (this.arbetsorderHasGeometrier() && this.arbetsorder.koordinaterVerifierade && this.isKoordinaterVerifieradeInvalid()) {
            this.arbetsorder.koordinaterVerifierade = false;
        }
    }

    getKartgeometriCount(name: string, subType: number): string {
        let count = 0;
        this.arbetsorder?.kartgeometrier?.forEach((kg) => {
            if ((subType === 2 && kg.subtyp2 === name) ||
                (subType === 3 && kg.subtyp3 === name)) {
                count++;
            }
        });

        return count > 0 ? `(${count.toString()})` : "";
    }

    getLatestVerksamhetshandelseTidpunkt(): void {
        if (!this.arbetsorder) {
            return;
        }

        this.arbetsorderProductvalService.refreshVerksamhetshandelse(
            VerksamhetshandelseTyp.Arbetsorder,
            Verksamhetshandelse.HamtaDrivningspunkterFranVirkeskopskontrakt,
            this.arbetsorder.guid)
            .subscribe(handelse => {
                if (handelse) {
                    this.latestKopieraDrivningspunkt = handelse.tidpunkt;
                }
            });
    }

    isAvlaggInvalid() {
        if (!this.arbetsorderHasGeometrier()) {
            return true;
        }
        const kgeos = this
            .arbetsorder
            .kartgeometrier
            .filter(Kartgeometrier.drivningspunktAvlagg.isOfSameSubtyp);
        return kgeos.length === 0 || kgeos.some(geo => !geo.tillganglighetsklass);
    }

    isKojplatsInvalid() {
        if (this.arbetsorder.isLeveransvirke) {
            return false;
        }

        if (!this.arbetsorderHasGeometrier()) {
            return true;
        }

        return this
            .arbetsorder
            .kartgeometrier
            .filter(Kartgeometrier.drivningspunktOvrigKojplats.isOfSameSubtyp)
            .length === 0;
    }

    isKoordinaterVerifieradeDisabled() {
        return this.isKoordinaterVerifieradeInvalid() || this.atgarderHasInvalidStatus();
    }

    isLarmkoordinatInvalid() {
        if (!this.arbetsorderHasGeometrier()) {
            return true;
        }

        return this.arbetsorder
            .kartgeometrier
            .filter(Kartgeometrier.drivningspunktOvrigLarmpunkt.isOfSameSubtyp)
            .length !== 1;
    }

    isTillganglig(): boolean {
        return this.arbetsorder.planeringsstatus === Planeringsstatus.Tillgangligt;
    }

    isVirkeskop() {
        return Anskaffningsformer.isVirkesKop(this.arbetsorder?.anskaffningsform);
    }

    koordinaterVerifieradeChanged(event) {
        this.updated.emit(event);
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.arbetsorderMediatorService.arbetsorder
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(changeEvent => {
                this.arbetsorder = changeEvent?.arbetsorderWrapper?.arbetsorder;
                this.getLatestVerksamhetshandelseTidpunkt();
                this.checkKoordinatorVerifierade();
            });
        this.arbetsorderMediatorService.readonly
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(readonly => {
                this.canEdit = !readonly;
            });
    }

    openAvlaggInfo(): boolean {
        this.modalService.open("Avlägg info", AvlaggVaginfoComponent, "xl", true, {
            arbetsorderId: this.arbetsorder.id
        });
        return true;
    }

    private atgarderHasInvalidStatus(): boolean {
        return this.atgardPlaneringsstatusService.isNotYetTillgangliga(this.arbetsorder.atgarder);
    }

    private isKoordinaterVerifieradeInvalid() {
        return this.isLarmkoordinatInvalid() ||
            this.isKojplatsInvalid() ||
            this.isAvlaggInvalid();
    }
}
